import React, { Dispatch, SetStateAction, useState } from 'react'

// ** MUI Imports
import { Avatar, Badge, IconButton, List, ListItemAvatar, ListItemButton, ListItemText } from '@mui/material'

import { Close } from 'mdi-material-ui'

// ** Type Imports
import { CatalogueCategoryType, CatalogueDataType } from 'src/lib/types/common/catalogue'

import { Package, Trash, XCircle } from 'react-feather'
import VariantEditor from './VariantEditor'

interface SelectedItemsListProps {
  data: CatalogueDataType[] | null | undefined
  saveStateObject?: (localSelection: CatalogueDataType[]) => void
  catalogueCatagory: CatalogueCategoryType | undefined
  isEditable?: boolean
  hideBorder?: boolean
}

const SelectedItemsList: React.FC<SelectedItemsListProps> = ({
  data,
  saveStateObject,
  catalogueCatagory,
  isEditable = true,
  hideBorder = false
}) => {
  const [isVariantEditorOpen, setIsVariantEditorOpen] = useState<boolean>(false)
  const [selectedProductToBeEdited, setSelectedProductToBeEdited] = useState<CatalogueDataType>()

  const handleDelete = (id: string | undefined) => {
    const filteredCatalogueData = data?.filter(item => item.id !== id)
    typeof saveStateObject !== 'undefined' && saveStateObject([...(filteredCatalogueData ?? [])])
  }

  const handleSaveVariantEditing = (listItem: CatalogueDataType | undefined) => {
    if (!listItem) return
    const modifiedCatalogueData = [...(data ?? [])]
    const indexToReplace = data?.findIndex((item: CatalogueDataType) => item?.id === listItem?.id)
    if (typeof indexToReplace === 'undefined' || indexToReplace === -1) return
    if (Boolean(listItem?.variants?.length)) {
      modifiedCatalogueData[indexToReplace] = listItem
    } else {
      modifiedCatalogueData.splice(indexToReplace, 1)
    }
    saveStateObject && saveStateObject([...modifiedCatalogueData])
  }

  const handleVariantEditClick = (listItem: CatalogueDataType) => {
    setIsVariantEditorOpen(true)
    setSelectedProductToBeEdited(listItem)
  }

  const renderHybridSelection = () => {
    return (
      <List sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3, padding: 0 }} component='nav'>
        {data?.map((listItem: CatalogueDataType, index: number) => {
          return (
            <div key={`${listItem?.id}_${index}`} className='flex gap-2 items-center justify-between'>
              <div className='flex gap-2 w-5/6 items-center'>
                <div className='flex items-center justify-center rounded-md border-2 border-[#F0F0F0] h-8 w-8'>
                  <Avatar
                    alt={catalogueCatagory}
                    src={listItem?.imageUrl}
                    sx={{ padding: 0, height: 32, width: 32, borderRadius: '6px', backgroundColor: 'transparent' }}
                  >
                    <Package className='w-6 h-6 stroke-[2px] text-[#E0E0E0]' />
                  </Avatar>
                </div>
                <div className='flex flex-col w-full' id={listItem?.id}>
                  <span className='overflow-ellipsis overflow-hidden text-sm text-coal-dark font-medium whitespace-nowrap'>
                    {listItem?.description}
                  </span>
                  {listItem?.id !== 'shopflo-all-products' && listItem?.id !== 'shopflo-all-collections' && (
                    <span className='overflow-ellipsis text-sm text-gray-dark font-medium whitespace-nowrap'>
                      {listItem?.variants?.length === 1 ? `${listItem?.variants?.[0]?.description} · ` : ''}
                      {`${listItem?.variants?.length ?? 0} of ${listItem?.totalVariantCount ?? 'many'} variants`}
                      <span
                        className='text-sm font-semibold text-primary-dark ml-[6px] cursor-pointer'
                        onClick={() => handleVariantEditClick(listItem)}
                      >
                        {isEditable ? 'Edit' : 'View'}
                      </span>
                    </span>
                  )}
                </div>
              </div>
              {Boolean(isEditable) ? (
                <IconButton
                  aria-label='close'
                  onClick={() => {
                    handleDelete(listItem?.id)
                  }}
                  sx={{
                    position: 'relative',
                    right: 0,
                    top: 0,
                    color: theme => theme.palette.grey[500]
                  }}
                >
                  <Trash className='w-4 h-4 stroke-[2px] text-gray-dark' />
                </IconButton>
              ) : (
                <></>
              )}
            </div>
          )
        })}
      </List>
    )
  }

  const renderPedigreeSelection = () => {
    return (
      <List sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3, padding: 0 }} component='nav'>
        {data?.map((listItem: CatalogueDataType, index: number) => {
          return (
            <div key={`${listItem?.id}_${index}`} className='flex gap-2 w-full items-center justify-between'>
              <div className='flex w-11/12 gap-2 items-center'>
                <div className='flex items-center justify-center rounded-md border-2 border-[#F0F0F0] h-8 w-8'>
                  <Avatar
                    alt={catalogueCatagory}
                    src={listItem?.imageUrl}
                    sx={{ padding: 0, height: 32, width: 32, borderRadius: '6px', backgroundColor: 'transparent' }}
                  >
                    <Package className='w-6 h-6 stroke-[2px] text-[#E0E0E0]' />
                  </Avatar>
                </div>
                <span
                  id={listItem?.id}
                  className='overflow-ellipsis overflow-hidden text-sm text-coal-dark font-medium whitespace-nowrap'
                >
                  {listItem?.description}
                </span>
              </div>
              {Boolean(isEditable) ? (
                <IconButton
                  aria-label='close'
                  onClick={() => {
                    handleDelete(listItem?.id)
                  }}
                  sx={{
                    position: 'relative',
                    right: 0,
                    top: 0,
                    color: theme => theme.palette.grey[500]
                  }}
                >
                  <Trash className='w-4 h-4 stroke-[2px] text-gray-dark' />
                </IconButton>
              ) : (
                <></>
              )}
            </div>
          )
        })}
      </List>
    )
  }

  const chooseCatalogueCatagory = () => {
    switch (catalogueCatagory) {
      case 'PRODUCT_AND_VARIANT':
        return renderHybridSelection()
      default:
        return renderPedigreeSelection()
    }
  }

  return (
    <>
      <div className={`w-full gap-2 flex flex-col ${hideBorder ? '' : 'border-1 border-[#F0F0F0] p-2'} rounded-lg `}>
        {chooseCatalogueCatagory()}
      </div>
      {isVariantEditorOpen ? (
        <VariantEditor
          isEditable={isEditable}
          isOpen={isVariantEditorOpen}
          setIsOpen={setIsVariantEditorOpen}
          product={selectedProductToBeEdited}
          saveCatalogueData={handleSaveVariantEditing}
        />
      ) : (
        <></>
      )}
    </>
  )
}

export default SelectedItemsList
